import React, { useMemo, useState } from 'react';
import { Paper, Typography, Button, Stack, Divider, Pagination, PaginationItem, Box } from '@mui/material';
import { ReactComponent as ArrowLeft } from 'assets/icons/pagination_left_icon.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/pagination_right_icon.svg';
import NoCampaignsImage from 'assets/no_campaigns.svg';
import RabatBadge from 'assets/images/rabat_badge.svg';
import NoFilteredCampaignsImage from 'assets/no_filtered_campaigns.svg';
import AddIcon from '@mui/icons-material/Add';
import { CampaignCard, NoItemsPlaceholder, RouterLinkButton, Dialog } from 'components';
import { useCampaign, useIsMobile } from 'hooks';
import { generatePath, useNavigate } from 'react-router-dom';
import { CampaignFilters, CampaignStatus, RouteList, Creative, Campaign, CampaignState, BillingMethod } from 'types';
import { CampaignBasicStats } from 'types/Stats';
import { CAMPAIGNS_PER_PAGE } from 'shared';
import { getFilteredCampaigns, getCampaignStatus, getDateDescription, isDiscountActive } from 'utils';
import invariant from 'tiny-invariant';
import { FeedbackModal } from './components/FeedbackModal';

const getFilterText = (campaignFilter: CampaignFilters, isMobile: boolean) => {
  if (campaignFilter === CampaignFilters.ALL && isMobile) return 'Alle';
  return campaignFilter;
};

const sortCampaigns = (a: Campaign, b: Campaign) => {
  const statusA = getCampaignStatus({
    state: a?.state || CampaignState.draft,
    startDate: a?.startDate || null,
    endDate: a?.endDate || null,
    isStopped: a?.isStopped,
  });
  const statusB = getCampaignStatus({
    state: b?.state || CampaignState.draft,
    startDate: b?.startDate || null,
    endDate: b?.endDate || null,
    isStopped: b?.isStopped,
  });

  const statusOrder = {
    [CampaignStatus.active]: 4,
    [CampaignStatus.ready]: 3,
    [CampaignStatus.draft]: 2,
    [CampaignStatus.stopped]: 1,
    [CampaignStatus.over]: 0,
  };

  // group and sort by status due to priorities (statusOrder)
  if (statusOrder[statusA] > statusOrder[statusB]) {
    return -1;
  }

  if (statusOrder[statusA] === statusOrder[statusB]) {
    if (statusA === CampaignStatus.active) {
      // Running campaigns always on the top (Newest first)
      return a.createdAt > b.createdAt ? -1 : 1;
    } else if (statusA === CampaignStatus.ready) {
      // Campaigns that will start soon (next upcoming ones first)
      if (a?.startDate && b?.startDate) {
        return a?.startDate > b?.startDate ? -1 : 1;
      }
      // otherwise sort by creation date (Newest first)
      return a.createdAt > b.createdAt ? -1 : 1;
    } else if (statusA === CampaignStatus.draft) {
      // Draft state campaigns (Last edited or created ones first)
      if (a?.updatedAt && b?.updatedAt) {
        return a?.updatedAt > b?.updatedAt ? -1 : 1;
      }

      return a.createdAt > b.createdAt ? -1 : 1;
    }

    if (a?.endDate && b?.endDate) {
      // Orders that are over or that got stopped should be shown at the end (Newest first)
      return a.endDate > b?.endDate ? -1 : 1;
    }

    return a.createdAt > b?.createdAt ? -1 : 1;
  }

  return 1;
};

export const MyCampaigns = () => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { getCampaignListQuery, getCampaignListStatsQuery, deleteCampaignFromListMutation, duplicateCampaignMutation } =
    useCampaign();
  const [campaignFilter, setCampaignFilter] = useState<CampaignFilters>(CampaignFilters.ALL);
  const [page, setPage] = useState(1);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const showDiscountActive = isDiscountActive();

  // Create a map of campaign stats for quick lookup
  const statsMap = useMemo(() => {
    const map = new Map<number, CampaignBasicStats>();
    if (getCampaignListStatsQuery.data?.stats) {
      getCampaignListStatsQuery.data.stats.forEach((stat) => {
        map.set(stat.campaignId, stat);
      });
    }
    return map;
  }, [getCampaignListStatsQuery.data]);

  const getPaginatedCampaigns = (campaigns: React.ReactNode[]) => {
    const startIndex = (page - 1) * CAMPAIGNS_PER_PAGE;
    const endIndex = page * CAMPAIGNS_PER_PAGE;
    return campaigns.slice(startIndex, endIndex);
  };

  const { data, isFetched } = getCampaignListQuery;
  const sortedCampaigns = data?.campaigns.sort(sortCampaigns);

  const userHasCampaigns = isFetched && sortedCampaigns?.length !== 0;

  const handleChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const showDeleteCampaignDialog = (campaignId: number) => {
    setSelectedCampaignId(campaignId);
    setIsConfirmDeleteDialogOpen(true);
  };

  const hideDeleteCampaignDialog = () => {
    setSelectedCampaignId(null);
    setIsConfirmDeleteDialogOpen(false);
  };

  const deleteCampaignHandler = async () => {
    invariant(selectedCampaignId);
    deleteCampaignFromListMutation.mutate(selectedCampaignId);
    hideDeleteCampaignDialog();
  };

  const filteredCampaigns = useMemo(() => {
    if (isFetched && sortedCampaigns) {
      return getFilteredCampaigns(campaignFilter, sortedCampaigns);
    } else return [];
  }, [campaignFilter, sortedCampaigns, isFetched]);

  const getCampaignCard = ({
    campaignId,
    title,
    status,
    dateDescription,
    hasLowPerformance,
    firstCreative,
    reportId,
    invoicedAmount,
    budget,
    billingMethod,
    stats,
  }: {
    campaignId: number;
    title: string;
    status: CampaignStatus;
    dateDescription: string;
    hasLowPerformance: boolean;
    firstCreative?: Creative | null;
    reportId?: string;
    invoicedAmount: number | null;
    budget?: number;
    billingMethod?: BillingMethod;
    stats?: CampaignBasicStats | null;
  }) => (
    <CampaignCard
      id={campaignId}
      status={status}
      key={campaignId}
      title={title}
      dateDescription={dateDescription}
      description={`Auftragsnummer: ${campaignId}`}
      editPath={generatePath(RouteList.Campaign, { campaignId: String(campaignId) })}
      viewPath={generatePath(RouteList.CampaignOverview, { campaignId: String(campaignId) })}
      onDelete={() => showDeleteCampaignDialog(campaignId)}
      duplicate={{
        onDuplicate: () => duplicateCampaignMutation.mutate(campaignId),
        isDuplicationLoading: duplicateCampaignMutation.isPending,
        duplicatingId: duplicateCampaignMutation.variables,
      }}
      firstCreative={firstCreative}
      hasLowPerformance={hasLowPerformance}
      reportId={reportId}
      invoicedAmount={invoicedAmount}
      budget={budget}
      billingMethod={billingMethod}
      stats={stats}
    />
  );

  return (
    <Paper>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        p={5}
        gap={isMobile ? 5 : 0}
      >
        <Typography variant="h2">Meine Kampagnen</Typography>
        {userHasCampaigns && (
          <RouterLinkButton
            to={RouteList.CampaignCreate}
            variant="contained"
            startIcon={<AddIcon sx={(theme) => ({ fontSize: `${theme.spacing(6)} !important` })} />}
          >
            neue kampagne erstellen
          </RouterLinkButton>
        )}
      </Stack>
      <Divider />
      <Stack sx={{ p: 5 }} gap={6}>
        {userHasCampaigns && (
          <Stack direction={'row'} justifyContent={'flex-end'} gap={4}>
            {(Object.keys(CampaignFilters) as Array<keyof typeof CampaignFilters>).map((key) => (
              <Button
                onClick={() => setCampaignFilter(CampaignFilters[key])}
                key={key}
                sx={(theme) => ({
                  borderRadius: 0,
                  borderBottom:
                    campaignFilter === CampaignFilters[key] ? `1px solid ${theme.palette.primary.main}` : 'none',
                })}
              >
                {getFilterText(CampaignFilters[key], isMobile)}
              </Button>
            ))}
          </Stack>
        )}
        {userHasCampaigns ? (
          filteredCampaigns.length > 0 ? (
            <>
              {getPaginatedCampaigns(
                filteredCampaigns.map(
                  ({
                    id,
                    name,
                    state,
                    startDate,
                    endDate,
                    updatedAt,
                    creatives,
                    hasLowPerformance,
                    reportId,
                    invoicedAmount,
                    isStopped,
                    stoppedAt,
                    budget,
                    billingMethod,
                  }) => {
                    const status = getCampaignStatus({
                      state,
                      startDate,
                      endDate,
                      isStopped,
                    });
                    const endDescriptionDate = status === CampaignStatus.stopped ? stoppedAt : endDate;

                    const dateDescription = getDateDescription(status, startDate, endDescriptionDate, updatedAt);
                    const firstCreative = creatives && creatives.length > 0 ? creatives[0] : null;
                    const campaignStats = statsMap.get(id) ?? null;

                    return getCampaignCard({
                      campaignId: id,
                      title: name,
                      status,
                      dateDescription,
                      hasLowPerformance,
                      firstCreative,
                      reportId,
                      invoicedAmount,
                      budget,
                      billingMethod,
                      stats: campaignStats,
                    });
                  }
                )
              )}
              <Stack alignItems={'center'}>
                <Pagination
                  page={page}
                  onChange={handleChange}
                  count={Math.ceil(filteredCampaigns.length / CAMPAIGNS_PER_PAGE)}
                  color="primary"
                  sx={(theme) => ({
                    backgroundColor: 'primary.light',
                    width: 'fit-content',
                    borderRadius: theme.spacing(10),
                    height: theme.spacing(8),
                    display: 'flex',
                  })}
                  renderItem={(item) => <PaginationItem slots={{ previous: ArrowLeft, next: ArrowRight }} {...item} />}
                />
              </Stack>
              <Dialog
                open={isConfirmDeleteDialogOpen}
                title="Bist du sicher, dass du die Kampagne löschen möchtest?"
                content=""
                primaryActionTitle="Ja, löschen"
                secondaryActionTitle="Nein, abbrechen"
                onSecondaryActionClick={hideDeleteCampaignDialog}
                onPrimaryActionClick={deleteCampaignHandler}
              />
            </>
          ) : (
            <NoItemsPlaceholder
              image={NoFilteredCampaignsImage}
              imageAlt="No filtered campaigns"
              title={'Hier gibt es noch keine Kampagnen'}
              button={{
                type: 'text',
                text: 'alle kampagnen anzeigen',
                onClick: () => setCampaignFilter(CampaignFilters.ALL),
              }}
            />
          )
        ) : (
          <NoItemsPlaceholder
            image={NoCampaignsImage}
            imageAlt="No campaigns"
            title={
              <>
                Starte jetzt deine erste Kampagne <br /> in wenigen Schritten!
              </>
            }
            subTitle={
              showDiscountActive ? (
                <>
                  Profitiere von unserem Rabatt von 50 % auf alle Kampagnen,
                  <br /> die bis Ende 2025 erstellt werden.
                </>
              ) : null
            }
            button={{
              type: 'contained',
              text: 'kampagne erstellen',
              onClick: () => navigate(generatePath(RouteList.CampaignCreate)),
            }}
            badge={
              showDiscountActive ? (
                <Box
                  position="absolute"
                  component="img"
                  sx={{ left: '50%', transform: 'translateX(-155px)', top: -20 }}
                  src={RabatBadge}
                  alt="Rabat Badge"
                ></Box>
              ) : null
            }
          />
        )}
      </Stack>
      <FeedbackModal />
    </Paper>
  );
};
